'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Property {
  id: string;
  title: string;
  location: string;
  price: number;
  beds: number;
  baths: number;
  area: number;
  type: string;
  image: string;
  imageAlt: string;
  badges: string[];
  agent: string;
  agency: string;
  pricePerSqft: number;
  yield: number;
  status: string;
  furnished: string;
}

interface Props {
  property: Property;
  viewMode: 'grid' | 'list';
}

function formatPrice(price: number) {
  if (price >= 1000000) return `AED ${(price / 1000000).toFixed(1)}M`;
  return `AED ${(price / 1000).toFixed(0)}K`;
}

const badgeMap: Record<string, { label: string; className: string }> = {
  luxury: { label: 'Luxury', className: 'badge-luxury' },
  verified: { label: '✓ Verified', className: 'badge-verified' },
  featured: { label: 'Featured', className: 'badge-featured' },
  new: { label: 'New', className: 'badge-new' },
};

export default function PropertyCard({ property, viewMode }: Props) {
  const [saved, setSaved] = useState(false);

  if (viewMode === 'list') {
    return (
      <Link href="/property-details-page" className="card-base group flex overflow-hidden">
        <div className="relative w-72 flex-shrink-0 overflow-hidden">
          <AppImage
            src={property.image}
            alt={property.imageAlt}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="280px"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-transparent" />
          <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
            {property.badges.slice(0, 2).map((b) => (
              <span key={`${property.id}-list-badge-${b}`} className={badgeMap[b]?.className ?? 'badge-verified'}>
                {badgeMap[b]?.label ?? b}
              </span>
            ))}
          </div>
          <button
            onClick={(e) => { e.preventDefault(); setSaved(!saved); }}
            className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${saved ? 'bg-accent text-white' : 'bg-black/30 text-white hover:bg-black/50'}`}
          >
            <Icon name="HeartIcon" size={14} />
          </button>
        </div>

        <div className="flex-1 p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-bold text-foreground text-base line-clamp-1">{property.title}</h3>
                <p className="text-muted-foreground text-sm flex items-center gap-1 mt-0.5">
                  <Icon name="MapPinIcon" size={13} />{property.location}
                </p>
              </div>
              <div className="text-right">
                <div className="property-price">{formatPrice(property.price)}</div>
                <div className="text-xs text-muted-foreground">AED {property.pricePerSqft}/sqft</div>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-foreground/80 mt-3">
              <div className="flex items-center gap-1.5">
                <Icon name="HomeIcon" size={14} className="text-primary" />
                <span className="font-medium">{property.beds > 0 ? `${property.beds} Beds` : 'Studio'}</span>
              </div>
              <span className="text-border">|</span>
              <span className="font-medium">{property.baths} Baths</span>
              <span className="text-border">|</span>
              <span className="font-medium">{property.area.toLocaleString()} sqft</span>
              <span className="text-border">|</span>
              <span className="font-medium">{property.type}</span>
              <span className="text-border">|</span>
              <span className="font-medium">{property.furnished}</span>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="UserIcon" size={13} className="text-primary" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">{property.agent}</div>
                <div className="text-xs text-muted-foreground">{property.agency}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-primary/10 text-primary font-bold px-2 py-1 rounded-lg">{property.yield}% Yield</span>
              <button onClick={(e) => e.preventDefault()} className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-white text-primary transition-all" aria-label="Call">
                <Icon name="PhoneIcon" size={14} />
              </button>
              <button onClick={(e) => e.preventDefault()} className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center hover:bg-green-500 hover:text-white text-green-700 transition-all" aria-label="WhatsApp">
                <Icon name="ChatBubbleLeftIcon" size={14} />
              </button>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href="/property-details-page" className="card-base group block">
      <div className="relative overflow-hidden h-52">
        <AppImage
          src={property.image}
          alt={property.imageAlt}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
        />
        <div className="absolute inset-0 bg-card-overlay" />
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
          {property.badges.slice(0, 2).map((b) => (
            <span key={`${property.id}-grid-badge-${b}`} className={badgeMap[b]?.className ?? 'badge-verified'}>
              {badgeMap[b]?.label ?? b}
            </span>
          ))}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); setSaved(!saved); }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${saved ? 'bg-accent text-white' : 'bg-black/30 text-white hover:bg-black/50'}`}
          aria-label="Save property"
        >
          <Icon name="HeartIcon" size={14} />
        </button>
        <div className="absolute bottom-3 left-3">
          <div className="text-white font-extrabold text-lg font-tabular">{formatPrice(property.price)}</div>
          <div className="text-white/70 text-xs">AED {property.pricePerSqft.toLocaleString()}/sqft</div>
        </div>
        <div className="absolute bottom-3 right-3 bg-primary/90 text-white text-xs font-bold px-2 py-1 rounded-lg">
          {property.yield}% Yield
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-bold text-foreground text-sm mb-1 line-clamp-1">{property.title}</h3>
        <p className="text-muted-foreground text-xs mb-3 flex items-center gap-1">
          <Icon name="MapPinIcon" size={12} />{property.location}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground/80 mb-3 pb-3 border-b border-border">
          <span className="font-medium">{property.beds > 0 ? `${property.beds} Beds` : 'Studio'}</span>
          <span className="text-border">|</span>
          <span className="font-medium">{property.baths} Baths</span>
          <span className="text-border">|</span>
          <span className="font-medium">{property.area.toLocaleString()} sqft</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <Icon name="UserIcon" size={11} className="text-primary" />
            </div>
            <span className="text-xs font-medium text-foreground">{property.agent}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={(e) => e.preventDefault()} className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-white text-primary transition-all" aria-label="Call">
              <Icon name="PhoneIcon" size={12} />
            </button>
            <button onClick={(e) => e.preventDefault()} className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center hover:bg-green-500 hover:text-white text-green-700 transition-all" aria-label="WhatsApp">
              <Icon name="ChatBubbleLeftIcon" size={12} />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}